
import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, UserRole } from '../types';
import { supabase } from '../services/supabaseClient';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, role: UserRole) => Promise<void>;
  completeOnboarding: () => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch or Create Profile
  const ensureProfile = async (authUser: any) => {
    if (!authUser) return null;

    try {
      // 1. Try to fetch existing profile
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (data) {
        const mappedUser: User = {
          id: data.id,
          name: data.full_name || authUser.email?.split('@')[0],
          email: data.email || authUser.email,
          role: data.role as UserRole,
          isOnboardingComplete: data.onboarding_complete,
        };
        setUser(mappedUser);
        return mappedUser;
      }

      // 2. If no profile found (e.g. first login after email verification), create it
      // We rely on metadata stored during signup
      const role = authUser.user_metadata?.role || 'client'; // Default to client if missing
      
      console.log('Profile missing, creating new profile for role:', role);

      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: authUser.id,
          email: authUser.email,
          role: role,
          full_name: authUser.email?.split('@')[0],
          onboarding_complete: false
        });

      if (!insertError) {
        const newUser: User = {
          id: authUser.id,
          name: authUser.email?.split('@')[0],
          email: authUser.email,
          role: role as UserRole,
          isOnboardingComplete: false,
        };
        setUser(newUser);
        return newUser;
      } else {
        console.error('Failed to auto-create profile:', insertError);
      }

    } catch (err) {
      console.error('Profile fetch/create error:', err);
    }
    return null;
  };

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user && mounted) {
        await ensureProfile(session.user);
      }
      if (mounted) setIsLoading(false);
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth State Change:', event);
      if (session?.user) {
        await ensureProfile(session.user);
      } else {
        setUser(null);
      }
      if (mounted) setIsLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setIsLoading(false);
      throw error;
    }
    
    // Explicitly ensure profile is loaded before resolving
    if (data.user) {
        await ensureProfile(data.user);
    }
  };

  const signup = async (email: string, password: string, role: UserRole) => {
    setIsLoading(true);
    // 1. Create Auth User with Metadata
    // We store the role in metadata so we can recover it later if the initial profile insert fails (due to RLS/email verification)
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
          data: { role: role } 
      }
    });

    if (authError) {
      setIsLoading(false);
      throw authError;
    }

    if (authData.user) {
      // 2. Attempt to Create Profile Row
      // This might fail if email verification is required and RLS denies insert.
      // That is OKAY. We will catch it and continue. The profile will be created on first login via ensureProfile.
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          email: email,
          role: role,
          full_name: email.split('@')[0],
          onboarding_complete: false
        });

      if (profileError) {
        console.warn('Profile creation deferred (likely pending email verification):', profileError.message);
      }
    }
  };

  const completeOnboarding = async () => {
    if (!user) return;
    
    const { error } = await supabase
      .from('profiles')
      .update({ onboarding_complete: true })
      .eq('id', user.id);

    if (!error) {
      setUser({ ...user, isOnboardingComplete: true });
    } else {
      console.error('Error updating onboarding status:', error);
      throw error;
    }
  };

  const logout = async () => {
    setIsLoading(true);
    await supabase.auth.signOut();
    setUser(null);
    setIsLoading(false);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, login, signup, completeOnboarding, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
